import { api } from '@data/store';
import {
  IncidentClosure,
  IncidentEntity,
  IncidentInput,
  IncidentPatch,
  IncidentPauseInput,
  IncidentReportAvailability,
  IncidentReportDraft,
  IncidentReportSendInput,
  IncidentReportSendResult,
  IncidentWorkEntity,
  ListResponse,
  ProductManager,
} from '@data/utils/types';

// Every incident on the organization's products, so the queue answers both its questions from one read.
export const fetchOrganizationIncidents = (
  organizationId: string,
  params?: { open?: boolean },
): Promise<ListResponse<IncidentEntity>> => api.get(`/organizations/${organizationId}/incidents`, { params });

/** One product's log, readable by anybody on the product. */
export const fetchProductIncidents = (productId: string): Promise<ListResponse<IncidentEntity>> =>
  api.get(`/products/${productId}/incidents`);

export const createIncident = (productId: string, body: IncidentInput): Promise<IncidentEntity> =>
  api.post(`/products/${productId}/incidents`, body);

// Absent fields are left alone, so one dialog sends one timestamp without blanking the rest.
export const updateIncident = (productId: string, incidentId: string, body: IncidentPatch): Promise<IncidentEntity> =>
  api.patch(`/products/${productId}/incidents/${incidentId}`, body);

/** Closing carries the moment it was fixed only for one nobody has said that about yet. */
export const closeIncident = (productId: string, incidentId: string, body: IncidentClosure): Promise<IncidentEntity> =>
  api.post(`/products/${productId}/incidents/${incidentId}/close`, body);

/** Putting a closed one back in the queue. When it was fixed survives it. */
export const reopenIncident = (productId: string, incidentId: string): Promise<IncidentEntity> =>
  api.post(`/products/${productId}/incidents/${incidentId}/reopen`, {});

/** Removing one logged in error. */
export const deleteIncident = (productId: string, incidentId: string): Promise<void> =>
  api.delete(`/products/${productId}/incidents/${incidentId}`);

/** Who booked time against one incident: real time entries, not a copy of them. */
export const fetchIncidentWork = (productId: string, incidentId: string): Promise<ListResponse<IncidentWorkEntity>> =>
  api.get(`/products/${productId}/incidents/${incidentId}/work`);

/** Pause writes answer with the whole incident, so the timeline cannot end up holding half a change. */
export const pauseIncident = (
  productId: string,
  incidentId: string,
  body: IncidentPauseInput,
): Promise<IncidentEntity> => api.post(`/products/${productId}/incidents/${incidentId}/pauses`, body);

export const updateIncidentPause = (
  productId: string,
  incidentId: string,
  pauseId: string,
  body: Partial<IncidentPauseInput>,
): Promise<IncidentEntity> => api.patch(`/products/${productId}/incidents/${incidentId}/pauses/${pauseId}`, body);

export const deleteIncidentPause = (productId: string, incidentId: string, pauseId: string): Promise<IncidentEntity> =>
  api.delete(`/products/${productId}/incidents/${incidentId}/pauses/${pauseId}`);

/** Which pairs will produce a report. A POST for a read, because a dozen ids in a query string finds a length limit. */
export const fetchIncidentReportAvailability = (
  organizationId: string,
  productIds: string[],
  sprintIds: string[],
): Promise<ListResponse<IncidentReportAvailability>> =>
  api.post(`/organizations/${organizationId}/incident-reports/availability`, { productIds, sprintIds });

/** One report as a PDF. Fetched through the client, which carries the session header an iframe cannot; 404 is silenced. */
export const fetchIncidentReportPdf = (productId: string, sprintId: string): Promise<Blob> =>
  api.get(`/products/${productId}/sprints/${sprintId}/incident-report`, {
    responseType: 'blob',
    silentStatuses: [404],
  });

/** The mail this selection would send, composed by the API so the words have one home. */
export const fetchIncidentReportDraft = (
  organizationId: string,
  productIds: string[],
  sprintIds: string[],
  firstName?: string,
): Promise<IncidentReportDraft> =>
  api.post(`/organizations/${organizationId}/incident-reports/draft`, { productIds, sprintIds, firstName });

export const sendIncidentReports = (
  organizationId: string,
  body: IncidentReportSendInput,
): Promise<IncidentReportSendResult> => api.post(`/organizations/${organizationId}/incident-reports/send`, body);

/** The tagged managers as addressable people, unlike the products module's candidates, which carry no email. */
export const fetchProductManagerRecipients = (
  organizationId: string,
  productIds: string[],
): Promise<ListResponse<ProductManager>> =>
  api.post(`/organizations/${organizationId}/product-managers`, { productIds });
